import { useEffect, useState } from "react"
import { Box, Button, Card, CardContent, MenuItem, Select, Stack, Typography, useMediaQuery } from "@mui/material"
import { Area, AreaChart, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

import useExternalFetch 		from "../api/apiExternal";
import { formatCurrency } 		from "../common/Helper";
import moment 					from 'moment'

import Loading 					from "./Loading"
import TrendingUp 				from "@mui/icons-material/TrendingUp";
import TrendingDown 			from "@mui/icons-material/TrendingDown";

const StockQuoteChart = (props) => {
	const externalFetch = useExternalFetch()

	const [fullQuoteHistory, setFullQuoteHistory]	= useState()
	const [quoteHistory, setQuoteHistory] 			= useState()
	const [currentReturn, setCurrentReturn]			= useState()
	const [selectedPeriod, setSelectedPeriod]		= useState('AT')

	const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('md'));

	// Busca as cotacoes sempre que o ticker for settado
	useEffect(() => {

		if (!props.ticker) return 

		externalFetch('https://financialmodelingprep.com/api/v3/historical-price-full/'+ props.ticker + ( props.ticker.match(/.*[0-9]$/) ? '.SA' : '' ) +'?serietype=line')
		.then(data => data.json())
		.then(data => {

			if (data.historical) {
				// Grava todas as cotacoes
				setFullQuoteHistory(data)

				// Seleciona uma amostra dos dados e calcula o retorno do período
				const parsedData = parseData(data, selectedPeriod)
				setQuoteHistory(parsedData)
				setCurrentReturn((((parsedData[parsedData.length-1].close/parsedData[0].close)-1)*100).toFixed(2))
			}
			else {
				setFullQuoteHistory({})
				setQuoteHistory({})
			}

		})
	}, [props.ticker])

	// Atualiza dados caso seja mudado o período
	useEffect(() => {
		if (fullQuoteHistory) {
			const parsedData = parseData(fullQuoteHistory, selectedPeriod)
			setQuoteHistory(parsedData)
			setCurrentReturn((((parsedData[parsedData.length-1].close/parsedData[0].close)-1)*100).toFixed(2))
		}
	}, [selectedPeriod])

	if (!props.ticker) {
		return <Typography variant="h6" color="error.main">Erro! Ticker não especificado.</Typography>
	}	

	return (
		<Box>
			<Stack direction={'row'} alignItems={'center'} display={'flex'} spacing={1}>
				<Typography variant="h6">COTAÇÃO</Typography>
				<Stack flex={1} direction={'row'} alignItems={'center'}>
					<Typography variant="h6" color={currentReturn > 0 ? "success.main" : "error.main"}>{currentReturn}%</Typography>
					{ currentReturn > 0 ? <TrendingUp color="success"/> : <TrendingDown color="error"/>}
				</Stack>
				
				{ isSmallScreen ? (
					<Select
						value={selectedPeriod}
						onChange={e => setSelectedPeriod(e.target.value)}
						sx={{ minWidth: 100, ml: 2 }}
						>
						<MenuItem value="1W">1 Semana</MenuItem>
						<MenuItem value="1M">1 Mês</MenuItem>
						<MenuItem value="6M">6 Meses</MenuItem>
						<MenuItem value="1Y">1 Ano</MenuItem>
						<MenuItem value="5Y">5 Anos</MenuItem>
						<MenuItem value="AT">Todo Período</MenuItem>
					</Select>
				): (
					<>
						<Button variant="contained" onClick={() => setSelectedPeriod('1W')} color={selectedPeriod === '1W' ? 'secondary' : 'primary'}>1S</Button>
						<Button variant="contained" onClick={() => setSelectedPeriod('1M')} color={selectedPeriod === '1M' ? 'secondary' : 'primary'}>1M</Button>
						<Button variant="contained" onClick={() => setSelectedPeriod('6M')} color={selectedPeriod === '6M' ? 'secondary' : 'primary'}>6M</Button>
						<Button variant="contained" onClick={() => setSelectedPeriod('1Y')} color={selectedPeriod === '1Y' ? 'secondary' : 'primary'}>1A</Button>
						<Button variant="contained" onClick={() => setSelectedPeriod('5Y')} color={selectedPeriod === '5Y' ? 'secondary' : 'primary'}>5A</Button>
						<Button variant="contained" onClick={() => setSelectedPeriod('AT')} color={selectedPeriod === 'AT' ? 'secondary' : 'primary'}>Todo Período</Button>
					</>
				)}
			</Stack>

			{quoteHistory?.length > 0 ? (
				<ResponsiveContainer width={'100%'} height={props.height ? props.height : 400}>
					<AreaChart 
						width={1000}
						height={400}
						data={quoteHistory}
						margin={{
							top: 50,
							right: 5,
							left: 20,
							bottom: 5,
						}}
						>
						<defs>
							<linearGradient id="colorUv" x1="0" y1="0" x2="0" y2="1">
								<stop offset="5%" stopColor="#8884d8" stopOpacity={0.8}/>
								<stop offset="95%" stopColor="#8884d8" stopOpacity={0}/>
							</linearGradient>
						</defs>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis dataKey="date" tickFormatter={(item) => moment(item).format('MM/YY')} style={{fontSize: 10}}/>
						<YAxis 
							yAxisId="left" 
							tickFormatter={(item) => formatCurrency(item.toFixed(2), 'SMALL')} 
							style={{fontSize: 10}} 
							domain={[dataMin => dataMin * 0.9, dataMax => dataMax * 1.1]} />
						<Area yAxisId="left" connectNulls type="monotone" dataKey="close" dot={false} stroke="#8884d8" fill="url(#colorUv)"/>
						<Tooltip content={<CustomTooltip/>} />
					</AreaChart>
				</ResponsiveContainer>
			) : (
				quoteHistory ? <Box height={400} display={'flex'} alignItems={'center'} justifyContent={'center'}><Typography>No history found!</Typography></Box> : <Loading/>
			)}
		</Box>
		
	)
}

const CustomTooltip = ({ active, payload, label }) => {
	return (
		<Card>
			<CardContent>
				<Stack spacing={1}>
					<Typography align="center" sx={{fontWeight: 'bold'}}>Cotação em {new Date(label).toLocaleDateString('pt-BR', {timeZone: 'UTC'})}</Typography>
					<Typography align="center" sx={{color: payload[0]?.stroke}}>{formatCurrency(payload[0]?.value, 'SMALL')}</Typography>
				</Stack>
			</CardContent>
		</Card>
	)
}

const parseData = (pData, pPeriod) => {
	let originalData	= pData.historical.sort((a,b) => new Date(a.date) - new Date(b.date))
	let slices			= 60

	// Define a data inicial para filtro e o número de amostras
	let dateLimit = moment()
	switch (pPeriod) {
		case '1W' : 
			dateLimit	= moment().subtract(7, 'days');
			slices		= 0
			break;
		case '1M' : 
			dateLimit = moment().subtract(1, 'months'); 
			slices 		= 0
			break;
		case '6M' : 
			dateLimit = moment().subtract(6, 'months'); 
			slices		= 5
			break;
		case '1Y' : 
			dateLimit = moment().subtract(1, 'years'); 
			slices		= 5
			break;
		case '5Y' : 
			dateLimit = moment().subtract(5, 'years'); 
			slices		= 30
			break;
	}

	// Se não for selecionado TODO PERIODO, filtro de acordo a data adequada
	if (pPeriod !== 'AT') originalData = originalData.filter(d => moment(d.date) > dateLimit)

	let parsedData 		= slices > 0 ? [] : originalData
	let currentSlice	= [];
	let counter			= 0

	// Percorre o array de acordo com o número de amostras configurado
	slices > 0 && originalData.map(d => {
		if (counter > slices) {
			// Ordena o array por valor para pegar a mediana e evitar outliers
			currentSlice = currentSlice.sort((a,b) => a.close - b.close)
			const index = Math.round(currentSlice.length/2)

			// Adiciona no array de retorno
			parsedData.push({
				date 	: currentSlice[index].date, 
				close	: currentSlice[index].close, 
			})

			// Zera os valores de controle
			currentSlice 	= []
			counter			= 0
		}
		
		currentSlice.push(d)
		counter++
	})

	return parsedData
}

export default StockQuoteChart